import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useGetApiChannelConnections,
} from "@/lib/api/generated/channels/channels";
import {
  usePostApiWorkflowPolicies,
} from "@/lib/api/generated/workflows/workflows";
import {
  useGetApiWorkflowDefinitions,
} from "@/lib/api/generated/workflows/workflows";
import type {
  ChannelConnectionRecord,
  JsonObject,
  WorkflowDefinition,
} from "../../../../../packages/shared-types/src/index.js";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";

const COMMON_ROLE_OPTIONS = ["member", "employee", "sales", "support"] as const;

const workflowPolicySchema = z.object({
  workflowKey: z.string().min(1, "Workflow is required"),
  enabled: z.boolean().default(true),
  allowedChannels: z.array(z.string()).default([]),
  allowedRoles: z.array(z.string()).default([]),
  config: z.string().default(""),
});

type WorkflowPolicyForm = z.infer<typeof workflowPolicySchema>;

export function CreateWorkflowPolicyPage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const { data: definitions } = useGetApiWorkflowDefinitions<WorkflowDefinition[]>({
    query: { enabled: Boolean(tenantId) },
  });
  const { data: channelConnections } = useGetApiChannelConnections<ChannelConnectionRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });
  const mutation = usePostApiWorkflowPolicies();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const channelOptions = useMemo(
    () =>
      Array.from(
        new Set((channelConnections ?? []).map((entry) => entry.channelType).filter(Boolean)),
      ).toSorted(),
    [channelConnections],
  );
  const roleOptions = useMemo(() => [...COMMON_ROLE_OPTIONS], []);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<WorkflowPolicyForm>({
    resolver: zodResolver(workflowPolicySchema),
    defaultValues: {
      enabled: true,
      allowedChannels: [],
      allowedRoles: [],
      config: "",
    },
  });

  const allowedChannels = watch("allowedChannels");
  const allowedRoles = watch("allowedRoles");

  const onSubmit = async (data: WorkflowPolicyForm) => {
    try {
      let config: JsonObject | undefined;
      if (showAdvanced && data.config.trim()) {
        try {
          config = JSON.parse(data.config);
        } catch {
          toast.error("Invalid JSON in advanced config");
          return;
        }
      }

      await mutation.mutateAsync({
        data: {
          tenantId: tenantId!,
          workflowKey: data.workflowKey,
          enabled: data.enabled,
          allowedChannels: data.allowedChannels,
          allowedRoles: data.allowedRoles,
          config,
        },
      });
      toast.success("Governance policy saved");
      navigate("/workspace/workflows");
    } catch {
      toast.error("Failed to save governance policy");
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-lg font-semibold mb-6" style={{ color: "var(--text-strong)" }}>
        Add Governance Policy
      </h1>
      <Card>
        <CardHeader>
          <CardTitle>Policy Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
            <FormField label="Workflow" error={errors.workflowKey?.message}>
              <select {...register("workflowKey")} className="form-input">
                <option value="">Select a workflow...</option>
                {definitions?.map((def) => (
                  <option key={def.workflowKey} value={def.workflowKey}>
                    {def.name} ({def.workflowKey})
                  </option>
                ))}
              </select>
            </FormField>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs font-medium" style={{ color: "var(--text-strong)" }}>
                <input type="checkbox" {...register("enabled")} />
                Enabled
              </label>
            </div>

            <SelectionField
              label="Allowed Channels"
              helper={
                allowedChannels.length === 0
                  ? "No channel restriction."
                  : `Restricted to: ${allowedChannels.join(", ")}`
              }
            >
              {channelOptions.length === 0 ? (
                <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                  No channel connections configured for this tenant yet.
                </p>
              ) : (
                <ToggleGroup
                  options={channelOptions}
                  selected={allowedChannels}
                  onToggle={(value) =>
                    setValue("allowedChannels", toggleListValue(allowedChannels, value), {
                      shouldDirty: true,
                    })
                  }
                />
              )}
            </SelectionField>

            <SelectionField
              label="Allowed Roles"
              helper={
                allowedRoles.length === 0
                  ? "No role restriction."
                  : `Restricted to: ${allowedRoles.join(", ")}`
              }
            >
              <ToggleGroup
                options={roleOptions}
                selected={allowedRoles}
                onToggle={(value) =>
                  setValue("allowedRoles", toggleListValue(allowedRoles, value), {
                    shouldDirty: true,
                  })
                }
              />
            </SelectionField>

            <div className="rounded-[var(--radius-sm)] border border-border p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
                    Advanced JSON Overrides
                  </span>
                  <span className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
                    Only use this when the workflow exposes extra policy config beyond the standard controls.
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdvanced((current) => !current)}
                >
                  {showAdvanced ? "Hide Advanced" : "Show Advanced"}
                </Button>
              </div>
              {showAdvanced ? (
                <div className="mt-4">
                  <FormField label="Config (JSON)" error={errors.config?.message}>
                    <textarea
                      {...register("config")}
                      placeholder='{ "key": "value" }'
                      className="form-input font-mono"
                      rows={6}
                    />
                  </FormField>
                </div>
              ) : null}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save Governance Policy"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => navigate("/workspace/workflows")}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function toggleListValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

function SelectionField({
  label,
  helper,
  children,
}: {
  label: string;
  helper: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </span>
      {children}
      <span className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
        {helper}
      </span>
    </div>
  );
}

function ToggleGroup({
  options,
  selected,
  onToggle,
}: {
  options: readonly string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const checked = selected.includes(option);
        return (
          <label
            key={option}
            className="flex items-center gap-2 rounded-[var(--radius-sm)] border px-3 py-2 cursor-pointer text-xs transition-colors"
            style={{
              background: checked ? "var(--accent-subtle)" : "transparent",
              borderColor: checked ? "var(--accent)" : "var(--border)",
              color: checked ? "var(--accent)" : "var(--text)",
            }}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(option)}
              className="shrink-0"
            />
            {option}
          </label>
        );
      })}
    </div>
  );
}

function FormField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
      {error ? (
        <span className="text-xs" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
